/**
 * Grand Quack Auto — screenshot share with social dialog.
 *
 * Captures the canvas, shows a modal with the screenshot preview and
 * one-click share buttons for Twitter, Facebook, Reddit.
 * On mobile: uses native Web Share API with image attachment.
 */

import type { World } from '../types';

let initialized = false;
let modalEl: HTMLElement | null = null;
let pulseTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Inject the pulse keyframes once at boot so pulseShareButton() can just
 * toggle a class. Lives here (not in index.html) to keep the share-button
 * UI self-contained and avoid contention with in-flight index.html WIP.
 */
function injectPulseStyles(): void {
  if (document.getElementById('share-btn-pulse-style')) return;
  const style = document.createElement('style');
  style.id = 'share-btn-pulse-style';
  style.textContent = `
    @keyframes share-btn-pulse {
      0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,215,0,0.7); background: rgba(0,0,0,0.4); }
      50%      { transform: scale(1.25); box-shadow: 0 0 24px 8px rgba(255,215,0,0.5); background: rgba(255,215,0,0.25); }
    }
    #share-btn.pulsing { animation: share-btn-pulse 0.7s ease-in-out infinite; border-color: #ffd700; }
  `;
  document.head.appendChild(style);
}

export function initShareButton(world: World): void {
  if (initialized) return;
  initialized = true;

  injectPulseStyles();

  const hud = document.getElementById('hud');
  if (!hud) return;

  const btn = document.createElement('button');
  btn.id = 'share-btn';
  btn.textContent = '📸';
  btn.title = 'Screenshot & Share';
  hud.appendChild(btn);

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    // Tapping the button stops any pulsing nag — we got the click.
    stopPulse();
    captureAndShowModal(world);
  });
}

/**
 * Flash the floating share button to draw the player's eye after a peak
 * moment (ABSOLUTE QUACKERY at x10 combo). Auto-clears after `durationMs`,
 * or earlier if the player taps the button.
 */
export function pulseShareButton(durationMs: number = 5000): void {
  const btn = document.getElementById('share-btn');
  if (!btn) return;
  btn.classList.add('pulsing');
  if (pulseTimeout !== null) clearTimeout(pulseTimeout);
  pulseTimeout = setTimeout(stopPulse, durationMs);
}

function stopPulse(): void {
  const btn = document.getElementById('share-btn');
  btn?.classList.remove('pulsing');
  if (pulseTimeout !== null) { clearTimeout(pulseTimeout); pulseTimeout = null; }
}

function getShareText(chaos: number): string {
  return `🦆 I reached ${chaos} chaos in Grand Quack Auto! Can you beat my duck? #GrandQuackAuto #VibeJam2026`;
}

const GAME_URL = 'https://grand-quack-auto.pages.dev';

/** Public entry point for photo mode (and share button click). */
export function captureAndShare(world: World): void {
  captureAndShowModal(world);
}

function captureAndShowModal(world: World): void {
  world.renderer.render(world.scene, world.camera);
  const canvas = world.renderer.domElement;
  const dataUrl = canvas.toDataURL('image/png');
  const chaos = Math.floor(world.chaos.points);
  const text = getShareText(chaos);

  // Mobile only: use native share sheet (iOS/Android). Never on desktop.
  const isMobile = 'ontouchstart' in window && window.innerWidth < 768;
  if (navigator.share && isMobile) {
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'grand-quack-auto.png', { type: 'image/png' });
      navigator.share({ text: text + '\n' + GAME_URL, files: [file] }).catch(() => {});
    });
    return;
  }

  // Desktop: show custom share modal.
  showShareModal(dataUrl, text, chaos);
}

function showShareModal(imageUrl: string, text: string, chaos: number): void {
  if (modalEl) modalEl.remove();

  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(GAME_URL);

  modalEl = document.createElement('div');
  modalEl.id = 'share-modal';
  modalEl.innerHTML = `
    <div class="share-card">
      <div class="share-header">
        <span>SHARE YOUR CHAOS</span>
        <button id="share-close">&times;</button>
      </div>
      <img class="share-preview" src="${imageUrl}" alt="Screenshot" />
      <div class="share-score">${chaos} CHAOS POINTS</div>
      <div class="share-buttons">
        <a class="share-btn-social share-twitter"
           href="https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}"
           target="_blank" rel="noopener">
          𝕏 Tweet
        </a>
        <a class="share-btn-social share-facebook"
           href="https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}"
           target="_blank" rel="noopener">
          f Facebook
        </a>
        <a class="share-btn-social share-reddit"
           href="https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}"
           target="_blank" rel="noopener">
          r Reddit
        </a>
        <button class="share-btn-social share-copy" id="share-copy-link">
          📋 Copy Link
        </button>
        <a class="share-btn-social share-download" href="${imageUrl}" download="grand-quack-auto.png">
          💾 Save Image
        </a>
      </div>
      <div class="share-tip">Tip: Save the image and attach it to your tweet!</div>
    </div>
  `;
  document.body.appendChild(modalEl);

  // Also copy image to clipboard if supported.
  try {
    fetch(imageUrl)
      .then(r => r.blob())
      .then(blob => {
        const item = new ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([item]);
      })
      .catch(() => {});
  } catch { /* clipboard API not available */ }

  // Event listeners.
  document.getElementById('share-close')?.addEventListener('click', closeModal);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) closeModal();
  });
  document.getElementById('share-copy-link')?.addEventListener('click', () => {
    navigator.clipboard?.writeText(GAME_URL + '\n' + text);
    const btn = document.getElementById('share-copy-link');
    if (btn) { btn.textContent = '✅ Copied!'; setTimeout(() => { btn.textContent = '📋 Copy Link'; }, 2000); }
  });
}

function closeModal(): void {
  modalEl?.remove();
  modalEl = null;
}
